import React from "react";
import ReactMarkdown from "react-markdown";

// ExpandableSection component
function ExpandableSection({ title, children }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="mb-3 sm:mb-4">
      <div
        className="cursor-pointer text-pl_prim_color_1 font-medium flex items-center gap-1 select-none text-sm sm:text-base hover:text-pl_prim_color_3 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-xs sm:text-sm">{expanded ? "▼" : "▶"}</span>{" "}
        <span>{title}</span>
      </div>
      {expanded && (
        <div className="p-2 sm:p-3 bg-pl_bg_light_4 rounded mt-1 text-xs sm:text-sm">
          {children}
        </div>
      )}
    </div>
  );
}

export default function TranslateLogDetail({ log }) {
  // Early return if no log is provided
  if (!log) {
    return (
      <div className="p-3 sm:p-4 lg:p-8 text-pl_txt_light_2 text-center flex items-center justify-center h-full">
        <div className="text-sm sm:text-base">
          Select a translate log to view details.
        </div>
      </div>
    );
  }

  // Function to render ALL available tags and properties from the log object
  const renderAllLogProperties = () => {
    // Get all properties from the log object
    const allProperties = Object.entries(log);
    const filteredProperties = allProperties.filter(([key]) => 
      !['id', '_id', 'task_id', 'timestamp', 'status','source_to_target'].includes(key)
    );
    
    if (filteredProperties.length === 0) {
      return null;
    }

    return (
      <div className="mb-3 sm:mb-4">
        {/* <h3 className="text-base sm:text-lg font-semibold mb-3 text-pl_txt_light_1">
          All Log Properties & Data
        </h3> */}
        <div className="space-y-3">
          {filteredProperties.map(([key, value]) => (
            <ExpandableSection title={key.replace(/_/g, " ")}>
              <div
                key={key}
                className="bg-pl_bg_light_5 rounded-lg p-3 sm:p-4 border-l-4 border-l-teal-500"
              >
                <div className="font-medium text-pl_txt_light_1 mb-3 capitalize text-lg">
                  {key.replace(/_/g, " ")}
                </div>

                {value === null ? (
                  <div className="text-pl_txt_light_3 italic">
                    No data available
                  </div>
                ) : value === undefined ? (
                  <div className="text-pl_txt_light_3 italic">Undefined</div>
                ) : typeof value === "object" ? (
                  <div className="space-y-2">
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <div
                        key={subKey}
                        className="bg-pl_bg_light_4 rounded p-3 border-l-2 border-l-teal-300"
                      >
                        <div className="font-medium text-pl_txt_light_1 mb-2 capitalize">
                          {subKey.replace(/_/g, " ")}
                        </div>
                        <div className="text-sm text-pl_txt_light_2">
                          {subValue === null ? (
                            <span className="text-pl_txt_light_3 italic">
                              null
                            </span>
                          ) : subValue === undefined ? (
                            <span className="text-pl_txt_light_3 italic">
                              undefined
                            </span>
                          ) : typeof subValue === "object" ? (
                            <pre className="whitespace-pre-wrap break-words bg-pl_bg_light_3 p-2 rounded text-xs overflow-x-auto">
                              {JSON.stringify(subValue, null, 2)}
                            </pre>
                          ) : Array.isArray(subValue) ? (
                            <div className="space-y-2">
                              {subValue.map((item, index) => (
                                <div
                                  key={index}
                                  className="bg-pl_bg_light_3 rounded p-2 text-xs"
                                >
                                  {typeof item === "object" ? (
                                    <pre className="whitespace-pre-wrap break-words">
                                      {JSON.stringify(item, null, 2)}
                                    </pre>
                                  ) : (
                                    <div className="prose prose-sm max-w-none">
                                      <ReactMarkdown>{String(item)}</ReactMarkdown>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="break-words prose prose-sm max-w-none">
                              <ReactMarkdown>{String(subValue)}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(value) ? (
                  <div className="space-y-2">
                    {value.map((item, index) => (
                      <div
                        key={index}
                        className="bg-pl_bg_light_4 rounded p-2 text-xs"
                      >
                        {typeof item === "object" ? (
                          <pre className="whitespace-pre-wrap break-words">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{String(item)}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-pl_txt_light_2 break-words prose prose-sm max-w-none">
                    <ReactMarkdown>{String(value)}</ReactMarkdown>
                  </div>
                )}
              </div>
            </ExpandableSection>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 sm:p-4 lg:p-8 flex-1 overflow-y-auto bg-pl_bg_light_3 h-full">
      {/* Header with basic log info */}
      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-pl_txt_light_1">
        Translate Log Details
      </h2>

      {/* Basic log information */}
      <div className="mb-4 p-3 bg-pl_bg_light_4 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-medium">ID:</span>{" "}
            {log.id || log._id || "N/A"}
          </div>
          <div>
            <span className="font-medium">Timestamp:</span>{" "}
            {log.timestamp || log.created_at || log.createdAt || "N/A"}
          </div>
          {/* <div>
            <span className="font-medium">Status:</span> {log.status || "N/A"}
          </div>
          <div>
            <span className="font-medium">User:</span>{" "}
            {log.user || log.user_id || log.userId || "N/A"}
          </div> */}
          <div>
            <span className="font-medium">source to target:</span>{" "}
            {log.source_to_target|| log.user_id || log.userId || "N/A"}
          </div>
        </div>
      </div>

      {/* Render ALL available properties and tags automatically */}
      {renderAllLogProperties()}
    </div>
  );
}
